/**
 * Sidebar Component
 * Contextual navigation sidebar with collapsible sections
 *
 * Features:
 * - Dark theme with neutral-900 background
 * - Active state with left border accent
 * - Smooth expand/collapse animations
 * - Mobile responsive (auto-collapse on small screens)
 * - Nested navigation support
 */

import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';

export interface SidebarItem {
  id: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  badge?: string | number;
  children?: SidebarItem[];
  /** Add separator line after this item */
  separator?: boolean;
}

export interface SidebarProps {
  items: SidebarItem[];
  width?: 'sm' | 'md' | 'lg';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  position?: 'left' | 'right';
  className?: string;
  /** Show mobile overlay when open */
  showMobileOverlay?: boolean;
  /** Callback when sidebar is closed (mobile) */
  onClose?: () => void;
}

export function Sidebar({
  items,
  width = 'md',
  collapsible = true,
  defaultCollapsed = false,
  position = 'left',
  className = '',
  showMobileOverlay = true,
  onClose,
}: SidebarProps) {
  // Auto-collapse on mobile by default
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return defaultCollapsed;
    return defaultCollapsed || window.innerWidth < 768;
  });
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  const widthStyles = {
    sm: isCollapsed ? 'w-16' : 'w-48',
    md: isCollapsed ? 'w-16' : 'w-64',
    lg: isCollapsed ? 'w-16' : 'w-80',
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const renderItem = (item: SidebarItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const indentClass = level === 0 ? '' : `ml-${level * 4}`;

    return (
      <div key={item.id} className={indentClass}>
        <div
          className={`
            relative flex items-center gap-3 px-4 py-3 rounded-r-lg
            transition-all duration-200 cursor-pointer group
            ${
              item.active
                ? 'bg-neutral-800 text-white border-l-4 border-primary-600'
                : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100'
            }
            ${isCollapsed ? 'justify-center px-2' : ''}
          `}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.id);
            } else if (item.onClick) {
              item.onClick();
            }
          }}
        >
          <a
            href={item.href}
            onClick={(e) => {
              if (item.onClick || hasChildren) {
                e.preventDefault();
              }
              // Close mobile menu on navigation
              if (window.innerWidth < 768 && onClose) {
                onClose();
              }
            }}
            className="flex items-center flex-1 min-w-0 focus:outline-none"
          >
            {item.icon && (
              <span className="flex-shrink-0">{item.icon}</span>
            )}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-sm font-medium truncate">
                  {item.label}
                </span>
                {item.badge !== undefined && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-600 text-white">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </a>

          {hasChildren && !isCollapsed && (
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-0' : '-rotate-90'
              }`}
            />
          )}
        </div>

        {hasChildren && isExpanded && !isCollapsed && (
          <div className="overflow-hidden transition-all duration-200 mt-1 space-y-1">
            {item.children!.map((child) => renderItem(child, level + 1))}
          </div>
        )}

        {/* Separator */}
        {item.separator && !isCollapsed && (
          <div className="my-3 border-t border-neutral-800"></div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {showMobileOverlay && isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => {
            setIsMobileOpen(false);
            onClose?.();
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${widthStyles[width]}
          ${position === 'right' ? 'order-last' : ''}
          bg-neutral-900 border-r border-neutral-800
          transition-all duration-300 ease-in-out
          flex flex-col
          fixed md:sticky top-0 h-screen z-50
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${className}
        `}
        aria-label="Sidebar navigation"
      >
        {/* Sidebar Header with Collapse Button */}
        {collapsible && (
          <div className="flex items-center justify-between p-4 border-b border-neutral-800">
            {!isCollapsed && (
              <span className="text-sm font-semibold text-neutral-100">Navigation</span>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-md hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 text-neutral-400 hover:text-neutral-100 transition-colors"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Sidebar Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1 scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-neutral-900">
          {items.map((item) => renderItem(item))}
        </nav>

        {/* Footer (optional branding or version) */}
        {!isCollapsed && (
          <div className="p-4 border-t border-neutral-800 text-center">
            <p className="text-xs text-neutral-500">HALMAS v1.0</p>
          </div>
        )}
      </aside>

      {/* Mobile Menu Toggle Button */}
      <button
        className="fixed bottom-4 right-4 md:hidden bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 z-40"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
    </>
  );
}
